/**
 * CompetitorFollowDialog — confirmation popup for « Suivre ce concurrent ».
 *
 * Flow:
 *  1. On open, calls POST /brands/:id/competitors/resolve-domain with the brand
 *     name + all source domains extracted from the current conversation turns.
 *     Cascade inside the endpoint: free token-match → TypeSafe Jev Choice → null.
 *  2. Pre-fills the domain field with the best candidate (editable).
 *  3. On confirm, calls POST /from-conversation with label + confirmed domain.
 *
 * Low confidence (< 0.5) or a failed resolution → empty field + amber hint.
 * The user always has the final word on the domain before saving.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../services/api';

interface Props {
  open: boolean;
  /** The brand name exactly as it appeared in the conversation. */
  label: string;
  /** All unique source domains from the conversation turns — fed to the resolver. */
  sourceDomains: string[];
  brandId: string;
  personaId?: string;
  launchId?: string;
  cellPath?: string;
  onClose: () => void;
  onSuccess: (label: string, domain: string) => void;
}

export function CompetitorFollowDialog({
  open,
  label,
  sourceDomains,
  brandId,
  personaId,
  launchId,
  cellPath,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();

  const [domain, setDomain] = useState('');
  const [resolveState, setResolveState] = useState<'loading' | 'done'>('loading');
  const [confidence, setConfidence] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve on open
  useEffect(() => {
    if (!open) return;
    setDomain('');
    setConfidence(null);
    setError(null);
    setResolveState('loading');

    api.post<{ domain: string | null; confidence: number; source: string }>(
      `/brands/${brandId}/competitors/resolve-domain`,
      { label, sourceDomains },
    ).then((res) => {
      setDomain(res.domain ?? '');
      setConfidence(res.confidence);
      setResolveState('done');
    }).catch(() => {
      setResolveState('done');
    });
  }, [open, brandId, label, sourceDomains]);

  const handleConfirm = async () => {
    if (!domain.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/brands/${brandId}/competitors/from-conversation`, {
        label, domain: domain.trim(), personaId, launchId, cellPath,
      });
      onSuccess(label, domain.trim());
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  const uncertain = resolveState === 'done' && (confidence === null || confidence < 0.5) && !domain;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {t('conversations.followDialogTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('conversations.followDialogSubtitle', { brand: label })}
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', mb: 0.5 }}>
            {t('conversations.followDialogNameLabel')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
        </Box>

        <Box sx={{ position: 'relative', mt: 1.5 }}>
          <TextField
            label={t('conversations.followDialogDomainLabel')}
            placeholder="exemple.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            size="small"
            fullWidth
            disabled={resolveState === 'loading'}
            autoFocus={resolveState === 'done'}
            helperText={
              resolveState === 'loading'
                ? t('conversations.followDialogResolving')
                : uncertain
                  ? t('conversations.followDialogUncertain')
                  : confidence !== null && confidence >= 0.5 && domain
                    ? t('conversations.followDialogResolved')
                    : ''
            }
            FormHelperTextProps={{
              sx: { color: uncertain ? 'warning.main' : 'success.main' },
            }}
          />
          {resolveState === 'loading' && (
            <CircularProgress size={14} sx={{ position: 'absolute', right: 10, top: 12 }} />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          {t('conversations.editorCancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!domain.trim() || resolveState === 'loading' || saving}
          onClick={handleConfirm}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          {t('conversations.followDialogConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
